import { forwardRef, React, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

import AccountInput from '../../../Component/AccountComponent/AccountInput.js';
import SignUpEmailInput from '../../../Component/AccountComponent/SignUpEmailInput.js';
// import SocialSecurityInputWrapper from '../../../Component/AccountComponent/SocialSecurityInputWrapper.js';
import FetchModule from '../../Share/Network/FetchModule.js';
import CountryCodeSelectBox from '../../../Component/AccountComponent/CountryCodeSelectBox.js';
import { usePDF } from '@react-pdf/renderer';
import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

// 테스트 모드: true로 설정하면 이메일 인증 자동 패스
const IS_TEST_MODE = false;

const SignUpInfo2 = forwardRef((props, ref) => {
	const { t } = useLanguage();
	const { isDark } = useTheme();
	const theme = isDark ? darkTheme : lightTheme;
	const [socialSecurityClickCount, setSocialSecurityClickCount] = useState(0);
	const [phoneClickCount, setPhoneClickCount] = useState(0);
	const [emailClickCount, setEmailClickCount] = useState(0);
	const [isInvalidSocialSecurityNumber, setIsInvalidSocialSecurityNumber] = useState(false);
	const [isInvalidPhoneNumber, setIsInvalidPhoneNumber] = useState(false);
	const [isEmptyEmail, setIsEmptyEmail] = useState(true);
	const [isInvalidEmail, setIsInvalidEmail] = useState(false);
	const [isSuccessToSendEmail, setIsSuccessToSendEmail] = useState(false);
	const [isCheckEmail, setIsCheckEmail] = useState(false);
	const [emailValue, setEmailValue] = useState('');
	const certifiedEmailRef = useRef('');
	const pendingEmailRef = useRef('');
	const [isClicked, setIsClicked] = useState(false);
	const [isAnimate, setIsAnimate] = useState(false);

	const IntervalRef = useRef(null);

	const TopLevelRef = useRef();
	const SocialSecurityRef = useRef();
	const PhoneRef = useRef();
	const EmailRef = useRef();
	const CountryCodeRef = useRef();
	const SocialSecurityPlanationRef = useRef();
	const PhonePlanationRef = useRef();
	const EmailPlanationRef = useRef();
	const LastCertifiedEmailRef = useRef();

	useImperativeHandle(ref, () => ({
		enableEnterAnimation: () => {
			setIsAnimate(true);
		},
		disableEnterAnimation: () => {
			setIsAnimate(false);
		},
		// getSocialSecurityNumber: () => {
		// 	return SocialSecurityRef.current.getValue();
		// },
		getPhoneNumber: () => {
			return PhoneRef.current.getValue();
		},
		getEmail: () => {
			return EmailRef.current.getValue();
		},
		getCountryCode: () => {
			return CountryCodeRef.current?.getValue() || '+82';
		},
		getCountryName: () => {
			return CountryCodeRef.current?.getCountry() || 'Korea';
		},
		isBlankSocialSecurityNumber: () => {
			setIsInvalidSocialSecurityNumber(false);
			SocialSecurityRef.current.isSocialSecurityBlank();
			SocialSecurityPlanationRef.current.style.opacity = 1;
		},
		isInvalidSocialSecurityNumber: () => {
			setIsInvalidSocialSecurityNumber(true);
			SocialSecurityRef.current.isSocialSecurityBlank();
			SocialSecurityPlanationRef.current.style.opacity = 1;
		},
		isBlankPhoneNumber: () => {
			setIsInvalidPhoneNumber(false);
			PhoneRef.current.setDefaultPlaceholder(t('signUp.phonePlaceholder'));
			PhoneRef.current.plusClickCount();
			PhonePlanationRef.current.style.opacity = 1;
		},
		isInvalidPhoneNumber: () => {
			setIsInvalidPhoneNumber(true);
			PhoneRef.current.setDefaultPlaceholder(t('signUp.phonePlaceholder'));
			PhonePlanationRef.current.style.opacity = 1;
		},
		// 전화번호 유효성 검증 (에러 표시하고 boolean 반환)
		isValidPhoneNumber: () => {
			const phone = (PhoneRef.current.getValue() || '').trim();

			PhoneRef.current.setDefaultPlaceholder(t('signUp.phonePlaceholder'));
			PhoneRef.current.plusClickCount();

			if (phone.length === 0) {
				setIsInvalidPhoneNumber(false);
				PhonePlanationRef.current.innerText = t('signUp.requiredField');
				PhonePlanationRef.current.style.opacity = 1;

				return false;
			}

			return true;
		},
		isBlankEmail: () => {
			setIsInvalidEmail(false);
			EmailRef.current.setDefaultPlaceholder(t('signUp.emailPlaceholder'));
			EmailRef.current.plusClickCount();
			EmailPlanationRef.current.style.opacity = 1;
		},
		isInvalidEmail: () => {
			setIsInvalidEmail(true);
			EmailRef.current.setDefaultPlaceholder(t('signUp.emailPlaceholder'));
			EmailPlanationRef.current.style.opacity = 1;
		},
		didNotCheckEmail: () => {
			setIsEmptyEmail(false);
			setIsInvalidEmail(false);
			EmailRef.current.setDefaultPlaceholder(t('signUp.emailPlaceholder'));
			EmailPlanationRef.current.style.opacity = 1;
		},
		getIsCheckEmail: () => {
			const currentEmail = EmailRef.current?.getValue() ?? '';

			return isCheckEmail && currentEmail === certifiedEmailRef.current;
		},
		setOpacity: (val) => {
			TopLevelRef.current.style.opacity = val;
			if (val === 0) TopLevelRef.current.setAttribute('inert', '');
			else TopLevelRef.current.removeAttribute('inert');
		},
		setLocation: (val) => {
			TopLevelRef.current.style.transform = `translateX(${val}%)`;
		}
	}));

	useEffect(() => {
		TopLevelRef.current.setAttribute('inert', '');
	}, []);

	// input 애니메이션 및 상태 처리
	useEffect(() => {
		if (phoneClickCount === 0 && emailClickCount === 0 && socialSecurityClickCount === 0) return;

		// 전화번호 유효성 검증 (실시간)
		const phone = (PhoneRef.current.getValue() || '').trim();

		if (phoneClickCount > 0) {
			if (phone.length === 0) {
				PhonePlanationRef.current.innerText = t('signUp.requiredField');
				PhonePlanationRef.current.style.opacity = 1;
			}
			else {
				// 유효한 전화번호: 에러 메시지 숨기고 border를 회색으로 설정
				PhonePlanationRef.current.style.opacity = 0;
				PhoneRef.current.reset(t('signUp.phonePlaceholder'));
			}
		}

		const email = (EmailRef.current.getValue() || '').trim();

		if (emailClickCount > 0 && email === '') {
		  EmailPlanationRef.current.style.color = theme.pointRed;
		  EmailPlanationRef.current.style.opacity = 1;
		}

		// if (SocialSecurityRef.current.getValue().length !== 14 && socialSecurityClickCount >= 0) {
		// 	SocialSecurityPlanationRef.current.style.color = theme.pointRed;
		// 	SocialSecurityPlanationRef.current.style.opacity = 1;
		// }
		// else {
		// 	SocialSecurityPlanationRef.current.style.opacity = 0;
		// }

	}, [phoneClickCount, emailClickCount, socialSecurityClickCount])

	useEffect(() => {
		if (isSuccessToSendEmail) EmailPlanationRef.current.style.color = theme.primary;
		else EmailPlanationRef.current.style.color = theme.pointRed;
	}, [isSuccessToSendEmail])

	useEffect(() => {
		if (!isClicked) {
			clearInterval(IntervalRef.current);
		}
	}, [isClicked])

	useEffect(() => {
	if (!LastCertifiedEmailRef.current) return;

	if (pendingEmailRef.current && emailValue === pendingEmailRef.current) {
		return;
	}

	if (emailValue !== LastCertifiedEmailRef.current) {
		setIsCheckEmail(false);
		setIsSuccessToSendEmail(false);
		setIsClicked(false);

		setIsInvalidEmail(false);
		setIsEmptyEmail(emailValue.trim() === '');

		EmailPlanationRef.current && (EmailPlanationRef.current.style.opacity = 0);

		clearInterval(IntervalRef.current);
		IntervalRef.current = null;
		pendingEmailRef.current = '';
	}
	}, [emailValue]);

	useEffect(() => {
	if (pendingEmailRef.current && emailValue === pendingEmailRef.current) {
		return;
	}

	if (certifiedEmailRef.current && emailValue !== certifiedEmailRef.current) {
		setIsCheckEmail(false);
		setIsSuccessToSendEmail(false);
		setIsClicked(false);

		setIsInvalidEmail(false);
		setIsEmptyEmail(emailValue.trim() === '');

		clearInterval(IntervalRef.current);
		IntervalRef.current = null;
		pendingEmailRef.current = '';

		EmailPlanationRef.current && (EmailPlanationRef.current.style.opacity = 0);
	}
	}, [emailValue]);

	// email형식 확인 정규식 //
	function isEmail(asValue) {
		const regExp = /^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,6}$/i;

		return regExp.test(asValue);
	}

	function clickCountFn(className, clickCount) {
		if (className === 'PhoneInput') setPhoneClickCount(clickCount);
		else if (className === 'EmailInput') setEmailClickCount(clickCount);
		else if (className === 'SocialSecurityInput') setSocialSecurityClickCount(clickCount);
	}

	// 이메일 사용 가능 여부 확인 //
	function emailAvailableCheck() {
		const email = EmailRef.current.getValue();

		setEmailValue(email);

		if (email === '') {
			setIsEmptyEmail(true);
			EmailRef.current.setDefaultPlaceholder(t('signUp.emailPlaceholder'));
			EmailPlanationRef.current.style.opacity = 1;
			return;
		}

		if (email === '' || !isEmail(email)) {
			setIsInvalidEmail(true);
			EmailRef.current.setDefaultPlaceholder(t('signUp.emailPlaceholder'));
			EmailPlanationRef.current.style.opacity = 1;
			return;
		}

		// 테스트 모드: 이메일 인증 자동 패스
		if (IS_TEST_MODE) {
			EmailPlanationRef.current.style.opacity = 1;
			setIsSuccessToSendEmail(true);
			setIsCheckEmail(true);
			certifiedEmailRef.current = email;
			setIsClicked(false);
			return;
		}

		EmailPlanationRef.current.style.opacity = 1;
		setIsSuccessToSendEmail(true);
		setIsClicked(true);

		const fetchModule = new FetchModule();
		(
			async () => {
				const responseData = await fetchModule.postData('account/asp/available/' + email, 'POST');

				if (responseData.status === 200) {
					EmailPlanationRef.current.style.opacity = 1;

					setIsSuccessToSendEmail(true);
					setIsClicked(true);
					pendingEmailRef.current = email;

					clearInterval(IntervalRef.current);
					startInterval(email);
				}
				else if (responseData.status === 205) {
					alert(t('signUp.emailAlreadyUsed'));
					setIsSuccessToSendEmail(false);
					setIsClicked(false);
				}
				else {
					alert(t('signUp.emailSendFailed'));
					setIsSuccessToSendEmail(false);
					setIsClicked(false);
				}
			})();
	}

	// 이메일 인증 상태 폴링 //
	function startInterval(email) {
		let count = 0;

		IntervalRef.current = setInterval(() => {
			
		const fetchModule = new FetchModule();
			(
				async () => {
					const responseData = await fetchModule.postData('account/asp/state/certification/' + email, 'POST');
					count += 1;

					if (responseData.status === 200) {
						setIsCheckEmail(true);
						setIsClicked(false);
						certifiedEmailRef.current = email;

						setEmailValue(email);

						pendingEmailRef.current = '';
						clearInterval(IntervalRef.current);
					}

					if (count === 180) clearInterval(IntervalRef.current);
				})
				();
		}, 1000)
	}

	return (
		<TopLevelWrapper ref={TopLevelRef} $animate={isAnimate}>
			<InputInfo className='phone'>{t('signUp.phone')}</InputInfo>
			<PhoneInputGroup>
            	<CountryCodeSelectBox ref={CountryCodeRef} />
            	<AccountInput ref={PhoneRef} className='PhoneInput' placeholder={t('signUp.phonePlaceholder')} type='text' clickCountFn={clickCountFn} 
			isValid={
				!isInvalidPhoneNumber &&
				(PhoneRef.current?.getValue() || '').trim().length > 0
			}/>
            </PhoneInputGroup>
			<AccountExplanation ref={PhonePlanationRef}>{t('signUp.requiredField')}</AccountExplanation>
			<InputInfo>{t('signUp.email')}</InputInfo>
			<SignUpEmailInput ref={EmailRef} isClicked={isClicked} setIsClicked={setIsClicked} className='EmailInput' placeholder={t('signUp.emailPlaceholder')} type='text' clickCountFn={clickCountFn} clickFn={emailAvailableCheck} text={t('signUp.emailCheck')} onEmailChange={setEmailValue} 
			isVerified={isCheckEmail && emailValue === certifiedEmailRef.current} />
			{
				isSuccessToSendEmail ?
					<AccountExplanation ref={EmailPlanationRef}>{isCheckEmail ? t('signUp.emailAvailable') : t('signUp.emailCertify')}</AccountExplanation>
					:
					<AccountExplanation ref={EmailPlanationRef}>{isInvalidEmail ? t('signUp.emailInvalid') : isEmptyEmail ? t('signUp.requiredField') : t('signUp.emailCheckRequired')}</AccountExplanation>
			}
			{/* <AccountExplanation ref={EmailPlanationRef}>{isInvalidEmail ? '이메일을 정확하게 입력해주세요' : '필수항목을 입력해주세요'}</AccountExplanation> */}
		</TopLevelWrapper>
	)
})

export default SignUpInfo2;

const startAnimation = keyframes`
	0% { opacity: 0; transform: translateX(150%); }
	10% { opacity: 0.1; transform: translateX(130%); }
	20% { opacity: 0.2; transform: translateX(100%); }
	30% { opacity: 0.3; transform: translateX(70%); }
	40% { opacity: 0.4; transform: translateX(40%); }
	50% { opacity: 0.5; transform: translateX(20%); }
	60% { opacity: 0.6; transform: translateX(0%); }
	70% { opacity: 0.7;}
	80% { opacity: 0.8; }
	90% { opacity: 0.9;}
	100% { opacity: 1;}
`;

const TopLevelWrapper = styled.div`
	position: absolute;
	display: flex;
	flex-direction: column;

	// justify-content: flex-start;
	// align-items: flex-start;

	width: 468px;
	// width: max(271px, 24.375vw);
	// width: max(250px, 24.375vw);

	margin-top: 159px;
	// margin-top: max(100px, 8.28125vw);

	background-color: ${props => props.theme.subBackground};

	opacity: 0;
	z-index: 1;
	transform: translateX(150%);

	animation: none;

	${(props) =>
			props.$animate &&
			css`
				animation: ${startAnimation} 0.5s ease-out;
	`}

	transition: all 0.3s ease-in-out;
`;

const InputInfo = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 16px;
	// font-size: max(12px, 0.7291666666666667vw);
	// line-height: 20px;

	// margin: 40px 0 0 0;
	margin: ${props => props.className === 'phone' ? `4px 0 0 0` : `19px 0 0 0`};
	// margin-top: ${props => props.className === 'phone' ? `4px` : `19px`};
	// margin-right: 0px;
	// margin-bottom: 0px;
	// margin-left: 0px;

	cursor: default;
	
	color: ${props => props.theme.text};
`;

const AccountExplanation = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;
	// font-size: max(12px, 0.625vw);
	// line-height: 17.38px;

	color: ${props => props.theme.pointRed};

	margin: 4px 0 0 0;

	opacity: 0;

  transition: all 0.5s ease-in-out;
`;

const PhoneInputGroup = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;

    width: 100%;
    gap: 10px;

    & > input {
        flex: 1;
        min-width: 0;
    }
`;